import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Create the context
const LangflowContext = createContext();

// Custom hook to use the Langflow context
export const useLangflow = () => {
  const context = useContext(LangflowContext);
  if (!context) {
    throw new Error('useLangflow must be used within a LangflowProvider');
  }
  return context;
};

// Langflow Provider component
//
// Supports two protocols, selected via `useAgUi`:
//  - legacy (default): browser talks directly to Langflow's pre-1.11
//    `/api/v1/run/{flowId}` REST/NDJSON-stream API. This is what every
//    product used before the AG-UI migration, and is still what products
//    whose Langflow flows haven't been moved to 1.11 need.
//  - AG-UI (`useAgUi`): browser talks to the frn-platform-backend
//    `/wellnessBuddy` proxy, which relays Langflow 1.11's AG-UI SSE event
//    stream. Requires `authToken` instead of a client-side Langflow API key.
export const LangflowProvider = ({
  children,
  useAgUi = false,
  baseUrl = 'https://langwatch.frnstaging.org',
  apiKey = 'sk-OMkVjSr49NmF5RmSKP7DORPTO9MXYbR7FPqszn35R5o',
  authToken,
  flowId = 'f6ad9339-e5fc-465c-9f2c-af20cba20d1b',
  userId,
  sessionId
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(userId);
  const [currentSessionId, setCurrentSessionId] = useState(sessionId || `session-${crypto.randomUUID()}`);

  useEffect(() => {
    if (sessionId) {
      setCurrentSessionId(sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    if (userId) {
      setCurrentUserId(userId);
    }
  }, [userId]);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Add a message to the conversation
  const addMessage = useCallback(message => {
    setMessages(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        ...message
      }
    ]);
  }, []);

  // Clear all messages
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  // Send message to Langflow server (legacy protocol, non-streaming)
  const sendMessage = useCallback(
    async (messageText, options = {}) => {
      if (!flowId) {
        throw new Error('Flow ID is required to send messages');
      }

      setIsLoading(true);
      setError(null);

      const userMessage = {
        type: 'user',
        content: messageText,
        sender: 'user'
      };
      addMessage(userMessage);

      try {
        const headers = {
          'Content-Type': 'application/json'
        };

        if (apiKey) {
          headers['x-api-key'] = `${apiKey}`;
        }

        const requestBody = {
          input_value: messageText,
          output_type: 'chat',
          input_type: 'chat',
          session_id: `${currentUserId}_${currentSessionId}`,
          tweaks: options.tweaks || {},
          ...options.additionalParams
        };

        const response = await fetch(`${baseUrl}/api/v1/run/${flowId}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        let assistantContent = '';
        if (data.outputs && data.outputs.length > 0) {
          const chatOutput = data.outputs.find(output => output.outputs && output.outputs.length > 0);

          if (chatOutput && chatOutput.outputs[0]) {
            const output = chatOutput.outputs[0];
            assistantContent =
              output.results?.message?.text || output.message?.text || output.text || JSON.stringify(output);
          }
        } else if (data.result) {
          assistantContent = data.result;
        } else {
          assistantContent = 'No response received from the server.';
        }

        const assistantMessage = {
          type: 'assistant',
          content: assistantContent,
          sender: 'assistant',
          rawResponse: data
        };
        addMessage(assistantMessage);

        return { userMessage, assistantMessage, rawResponse: data };
      } catch (err) {
        const errorMessage = err.message || 'Failed to send message';
        setError(errorMessage);

        const errorChatMessage = {
          type: 'error',
          content: `Error: ${errorMessage}`,
          sender: 'system'
        };
        addMessage(errorChatMessage);

        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [baseUrl, apiKey, flowId, addMessage, currentUserId, currentSessionId]
  );

  // Stream message to Langflow's legacy NDJSON stream (pre-1.11 protocol)
  const streamMessageLegacy = useCallback(
    async (messageText, onChunk, options = {}) => {
      if (!flowId) {
        throw new Error('Flow ID is required to stream messages');
      }

      setIsLoading(true);
      setError(null);

      const userMessage = {
        type: 'user',
        content: messageText,
        sender: 'user'
      };
      addMessage(userMessage);

      try {
        const headers = {
          'Content-Type': 'application/json',
          accept: 'application/json'
        };

        if (apiKey) {
          headers['x-api-key'] = apiKey;
        }

        const requestBody = {
          input_value: messageText,
          input_type: 'chat',
          output_type: 'chat',
          session_id: `${currentUserId}_${currentSessionId}`,
          tweaks: options.tweaks || {},
          ...options.additionalParams
        };

        const response = await fetch(`${baseUrl}/api/v1/run/${flowId}?stream=true`, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const assistantMessageId = Date.now() + Math.random();
        const assistantMessage = {
          id: assistantMessageId,
          type: 'assistant',
          content: '',
          sender: 'assistant',
          streaming: true,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, assistantMessage]);
        let assistantContent = '';
        let partialChunk = '';
        const decoder = new TextDecoder();

        try {
          // Use getReader() for better Safari compatibility
          const reader = response.body.getReader();

          while (true) {
            const { done, value } = await reader.read();

            const txt = partialChunk + decoder.decode(value);
            partialChunk = '';
            const events = txt.split('\n');
            if (!txt.endsWith('\n')) {
              partialChunk = events.pop();
            }
            events
              .filter(e => e !== '')
              .map(e => {
                const d = JSON.parse(e);
                if (d.event === 'token') {
                  assistantContent += d.data.chunk;
                } else if (d.event === 'add_message') {
                  if (d.data.sender !== 'Machine') return;
                  assistantContent = d.data.text;
                } else if (d.event === 'end') {
                }
              });
            setMessages(prev =>
              prev.map(msg => {
                return msg.id === assistantMessageId ? { ...msg, content: assistantContent } : msg;
              })
            );
            if (done) break;
          }
        } catch (streamError) {
          console.error('Streaming error:', streamError, response);
          throw streamError;
        }

        if (!assistantContent.trim()) {
          assistantContent = 'No response received from the server.';
        }

        setMessages(prev =>
          prev.map(msg =>
            msg.id === assistantMessageId ? { ...msg, content: assistantContent, streaming: false } : msg
          )
        );

        return { userMessage, assistantMessage: { ...assistantMessage, content: assistantContent, streaming: false } };
      } catch (err) {
        const errorMessage = err.message || 'Failed to stream message';
        setError(errorMessage);

        const errorChatMessage = {
          type: 'error',
          content: `Error: ${errorMessage}`,
          sender: 'system'
        };
        addMessage(errorChatMessage);

        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [baseUrl, apiKey, flowId, addMessage, currentUserId, currentSessionId]
  );

  // Stream a message via the frn-platform-backend Wellness Buddy proxy, which
  // relays Langflow 1.11's AG-UI SSE event stream. The Langflow API key stays
  // server-side; the browser never talks to Langflow directly.
  const streamMessageAgUi = useCallback(
    async (messageText, onChunk, options = {}) => {
      if (!flowId) {
        throw new Error('Flow ID is required to stream messages');
      }

      setIsLoading(true);
      setError(null);

      // Add user message immediately
      const userMessage = {
        type: 'user',
        content: messageText,
        sender: 'user'
      };
      addMessage(userMessage);

      // AG-UI can emit more than one distinct text message per run (e.g. text
      // before a tool call, then more text after) - each carries its own
      // messageId, so track a separate bubble per messageId rather than
      // assuming everything belongs to a single message.
      const bubbleIdByMessageId = new Map();
      const contentByBubbleId = new Map();
      const createdBubbleIds = [];

      const getOrCreateBubble = messageId => {
        if (bubbleIdByMessageId.has(messageId)) {
          return bubbleIdByMessageId.get(messageId);
        }
        const bubbleId = Date.now() + Math.random();
        bubbleIdByMessageId.set(messageId, bubbleId);
        contentByBubbleId.set(bubbleId, '');
        createdBubbleIds.push(bubbleId);
        setMessages(prev => [
          ...prev,
          {
            id: bubbleId,
            type: 'assistant',
            content: '',
            sender: 'assistant',
            streaming: true,
            timestamp: new Date().toISOString()
          }
        ]);
        return bubbleId;
      };

      const appendContent = (messageId, delta) => {
        const bubbleId = getOrCreateBubble(messageId);
        const updatedContent = (contentByBubbleId.get(bubbleId) || '') + delta;
        contentByBubbleId.set(bubbleId, updatedContent);
        setMessages(prev => prev.map(msg => (msg.id === bubbleId ? { ...msg, content: updatedContent } : msg)));
        return updatedContent;
      };

      const endBubble = messageId => {
        const bubbleId = bubbleIdByMessageId.get(messageId);
        if (bubbleId === undefined) return;
        setMessages(prev => prev.map(msg => (msg.id === bubbleId ? { ...msg, streaming: false } : msg)));
      };

      const appendStatus = statusText => {
        setMessages(prev => [
          ...prev,
          {
            id: Date.now() + Math.random(),
            timestamp: new Date().toISOString(),
            type: 'status',
            content: statusText,
            sender: 'system'
          }
        ]);
      };

      try {
        const response = await fetch(`${process.env.ROOT_URL}/wellnessBuddy`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`
          },
          body: JSON.stringify({
            flow_id: flowId,
            input_value: messageText,
            session_id: currentSessionId,
            ...options.additionalParams
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        let runError = null;
        let partialChunk = '';
        const decoder = new TextDecoder();
        const reader = response.body.getReader();

        while (true) {
          const { done, value } = await reader.read();

          const txt = partialChunk + decoder.decode(value || new Uint8Array(), { stream: !done });
          const lines = txt.split('\n');
          partialChunk = done ? '' : lines.pop();

          lines
            .filter(line => line.startsWith('data: '))
            .forEach(line => {
              const event = JSON.parse(line.slice('data: '.length));
              switch (event.type) {
                case 'TEXT_MESSAGE_START':
                  getOrCreateBubble(event.messageId);
                  break;
                case 'TEXT_MESSAGE_CONTENT': {
                  const delta = event.delta ?? '';
                  const updatedContent = appendContent(event.messageId, delta);
                  if (onChunk) onChunk(delta, updatedContent);
                  break;
                }
                case 'TEXT_MESSAGE_END':
                  endBubble(event.messageId);
                  break;
                case 'TOOL_CALL_START':
                  appendStatus('Using a tool…');
                  break;
                case 'RUN_ERROR':
                  runError = event.message || 'Langflow run error';
                  break;
                default:
                  break;
              }
            });

          if (done) break;
        }

        if (runError) {
          throw new Error(runError);
        }

        // Mark every bubble created during this run as done streaming (in case
        // a TEXT_MESSAGE_END was missed for one).
        setMessages(prev =>
          prev.map(msg => (createdBubbleIds.includes(msg.id) ? { ...msg, streaming: false } : msg))
        );

        const hasContent = [...contentByBubbleId.values()].some(content => content.trim());
        if (!hasContent) {
          addMessage({
            type: 'assistant',
            content: 'No response received from the server.',
            sender: 'assistant'
          });
        }

        return { userMessage };
      } catch (err) {
        const errorMessage = err.message || 'Failed to stream message';
        setError(errorMessage);

        // Remove the now-orphaned streaming placeholder(s) and surface an error message instead
        setMessages(prev => prev.filter(msg => !createdBubbleIds.includes(msg.id)));

        const errorChatMessage = {
          type: 'error',
          content: `Error: ${errorMessage}`,
          sender: 'system'
        };
        addMessage(errorChatMessage);

        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [authToken, flowId, addMessage, currentSessionId]
  );

  const streamMessage = useAgUi ? streamMessageAgUi : streamMessageLegacy;

  // Test connection to Langflow server (legacy protocol only)
  const testConnection = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const headers = {};
      if (apiKey) {
        headers['Authorization'] = `Bearer ${apiKey}`;
      }

      const response = await fetch(`${baseUrl}/api/v1/flows`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to connect to Langflow server';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl, apiKey]);

  const value = {
    // State
    isLoading,
    error,
    messages,

    // Configuration
    useAgUi,
    baseUrl,
    apiKey,
    flowId,
    currentSessionId,

    // Actions
    sendMessage,
    streamMessage,
    addMessage,
    clearMessages,
    clearError,
    setCurrentUserId,
    testConnection
  };

  return <LangflowContext.Provider value={value}>{children}</LangflowContext.Provider>;
};

export default LangflowProvider;
